import { get, getFile, post, postSSE, remove } from '@src/services/NetworkUtils'
import { saveBlob } from '@src/services/media'
import ReportEnquiryService from '@pages/ReportEnquiry/service'

// backoffice-service modules/enquiry_import (Bulk Enquiry v1, P1b + P2).
const BASE = '/v1/report-enquiry/import-batches'

// Only when the response names no file (Content-Disposition missing or not
// exposed by CORS). The real name is the server's: it is built per company.
const TEMPLATE_FALLBACK_NAME = 'bulk-enquiry-template.xlsx'

// GET /:batchId/rows takes at most 500 per page (enquiry_import.middlewares
// rowsValidators), the most rows a batch has today (BULK_ENQUIRY_MAX_ROWS).
const PENDING_PAGE_SIZE = 500
// A batch never has more rows than this many pages; a guard, not a limit.
const PENDING_MAX_PAGES = 20

const Service = {
  list: (params) => get(BASE, params),
  show: (batchId) => get(`${BASE}/${batchId}`),
  rows: (batchId, params) => get(`${BASE}/${batchId}/rows`, params),
  // multipart: company_id + file (.xlsx)
  upload: (formData) => post(BASE, formData, 'form-data'),
  removeRow: (batchId, rowId) => remove(`${BASE}/${batchId}/rows/${rowId}`),
  resyncConfig: (batchId) => post(`${BASE}/${batchId}/resync-config`),
  cancel: (batchId) => post(`${BASE}/${batchId}/cancel`),

  /**
   * Every row still waiting for files (incomplete and ready), all pages, as one
   * `{ data, paginator }` list: the rows the dropped files are matched against.
   */
  pendingRows: async (batchId) => {
    const byId = new Map()
    let page = 1
    let totalPages = 1
    do {
      // eslint-disable-next-line no-await-in-loop
      const res = await get(`${BASE}/${batchId}/rows`, {
        status: 'incomplete,ready',
        limit: PENDING_PAGE_SIZE,
        page,
      })
      ;(res?.data || []).forEach((row) => byId.set(row.id, row))
      totalPages = Number(res?.paginator?.total_pages) || 1
      page += 1
    } while (page <= totalPages && page <= PENDING_MAX_PAGES)
    const data = [...byId.values()]
    return { data, paginator: { page: 1, limit: data.length, total: data.length, total_pages: 1 } }
  },

  /**
   * Stage (or replace) one file of a row: POST /:batchId/rows/:rowId/files/:kind,
   * multipart `file`. typeFix: the MIME type to send a photo with when the
   * browser declared none the backend accepts (fileMatcher.fileFamily).
   * Resolves `{ data: { row_id, row_no, status, status_changed, missing,
   * replaced, file, files } }`: the row's files exactly as the rows API shows them.
   */
  stageFile: (batchId, rowId, kind, file, { typeFix = null, onUploadProgress } = {}) => {
    const formData = new FormData()
    const body = typeFix ? new File([file], file.name, { type: typeFix, lastModified: file.lastModified }) : file
    formData.append('file', body, file.name)
    return post(`${BASE}/${batchId}/rows/${rowId}/files/${kind}`, formData, 'form-data', undefined, {
      onUploadProgress,
    })
  },

  /**
   * Remove a row's saved file of one kind: DELETE …/files/:kind. Resolves
   * `{ data: { row_id, row_no, status, status_changed, missing, removed, files } }`
   * (removed false: there was no saved file any more).
   */
  unstageFile: (batchId, rowId, kind) => remove(`${BASE}/${batchId}/rows/${rowId}/files/${kind}`),

  /**
   * Process every ready row: POST /:batchId/process, answered as an SSE
   * stream (start, progress per row, then result or error; processEvents.js).
   * A refusal before the stream is a rejection with the JSON error
   * (errors.processRefusal). `signal` stops reading (the run goes on).
   */
  process: (batchId, onEvent, { signal } = {}) => postSSE(`${BASE}/${batchId}/process`, null, onEvent, { signal }),

  /**
   * Check every failed row again: POST /:batchId/retry-failed. Resolves the
   * batch item + { retried, now_ready, now_incomplete, now_invalid, reopened,
   * completed }.
   */
  retryFailed: (batchId) => post(`${BASE}/${batchId}/retry-failed`),

  // The template is built per company (its levels, its consent settings), and
  // so is its file name (Content-Disposition).
  downloadTemplate: async (companyId) => {
    const { blob, fileName } = await getFile(`${BASE}/template`, { company_id: companyId })
    saveBlob(blob, fileName || TEMPLATE_FALLBACK_NAME)
  },

  // Same membership-scoped option list as New Enquiry's entity picker.
  getCompanyOptions: ReportEnquiryService.getCompanyOptions,
}

export default Service
